import {
  LocalActionCounts,
  reduceActionCounts,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/utils/count-actions';
import {
  omitRejectedActions,
  RecommendationRowRef,
  resolveAutoAcceptedActions,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/utils/auto-accepted-actions';
import { RecommendationActionsSnapshot } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import { useMemo } from 'react';
import { useRecommendationActionsStore } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store';

interface Params {
  isAutoAccepted: boolean;
  rows: RecommendationRowRef[];
}

interface PublishableActions {
  /** Every row Publish acts on, for the confirmation copy. */
  counts: LocalActionCounts;
  /** Whether Publish has anything to send. */
  hasPublishableActions: boolean;
  /** The mutation payload. */
  publishableActions: RecommendationActionsSnapshot;
}

/**
 * What Publish will submit, with the tallies to confirm it by. Auto-accepted
 * screens act on every row (accepted by default) but publish only the prices to
 * apply, since their rejections are already persisted; everywhere else the
 * user's explicit edits are published as-is.
 */
export const usePublishableActions = ({
  isAutoAccepted,
  rows,
}: Params): PublishableActions => {
  const actions = useRecommendationActionsStore((state) => state.actions);

  return useMemo(() => {
    if (!isAutoAccepted) {
      const counts = reduceActionCounts(actions);

      return {
        counts,
        hasPublishableActions: counts.actionedCount > 0,
        publishableActions: actions,
      };
    }

    const effectiveActions = resolveAutoAcceptedActions(rows, actions);
    const publishableActions = omitRejectedActions(effectiveActions);

    return {
      counts: reduceActionCounts(effectiveActions),
      hasPublishableActions: Object.keys(publishableActions).length > 0,
      publishableActions,
    };
  }, [actions, isAutoAccepted, rows]);
};
